import qs from 'query-string';
import { AxiosRequestConfig } from 'axios';
import { CouponRequestDto } from '../../../types/coupons';
import SuperFetchV2 from '../../superFetchV2';
import SuperFetch from '../../superFetch';
import { CouponFindProductsResponseBodyDto, CouponsFindAllResponseBodyDto } from '../../../types/api/coupons';
import { CouponProductListOptionV2 } from '../../../types/coupons/product';
import { setRequestForm, setMultipartForm } from '../../common';

export const CouponApiV2 = {
  /**
   * `config` 는 타이핑 연동 조회를 위한 것이다 — `skipFullscreenLoader` 로 전역 로더를 끈다.
   * 페이지 목록처럼 한 번에 끝나는 조회는 로더가 맞으므로 기본값은 그대로 둔다.
   */
  getCouponList: (data: CouponRequestDto, config?: AxiosRequestConfig): Promise<CouponsFindAllResponseBodyDto> => {
    const queryString = qs.stringify(data, { skipNull: true, arrayFormat: 'comma' });
    return SuperFetchV2.get('/coupons?' + queryString, config);
  },
  getCouponProductList: (
    couponId: number,
    option: CouponProductListOptionV2,
  ): Promise<CouponFindProductsResponseBodyDto> => {
    const queryString = qs.stringify(option, { skipNull: true, arrayFormat: 'comma' });
    return SuperFetchV2.get(`/coupons/${couponId}/products?` + queryString);
  },

  /** [쿠폰 관리] 쿠폰 리스트 (V1 래핑) */
  getCouponManagementList: (data: Record<string, unknown>): Promise<{
    data: { couponList: Record<string, unknown>[]; totalCnt: number };
  }> => SuperFetch.post('/coupon/list', setRequestForm(data, true)),

  /** [쿠폰 관리] 쿠폰 리스트 엑셀 다운로드 (V1 래핑) */
  getCouponListExcelDownload: (data: Record<string, unknown>): Promise<{ data: { couponListExcelSheet: { data: number[] } } }> =>
    SuperFetch.post('/coupon/list/excel/download', setRequestForm(data, true)),

  /** 쿠폰 발급 내역 리스트 (V1 래핑) */
  getCouponIssueHistoryList: (data: Record<string, unknown>): Promise<{
    data: { couponIssueHistoryList: Record<string, unknown>[]; totalCnt: number; adminSelectList: { id: number; name: string }[] };
  }> => SuperFetch.post('/coupon/issueHistory/list', setRequestForm(data, true)),

  /** 쿠폰 발급 내역 상세 (V1 래핑) */
  getCouponIssueHistoryDetail: (data: Record<string, unknown>): Promise<{
    data: { couponIssueHistoryInfo: Record<string, unknown>; couponIssueHistoryItemList: Record<string, unknown>[]; totalCnt: number };
  }> => SuperFetch.post('/coupon/issueHistory/detail', setRequestForm(data, true)),

  /** [쿠폰 상세] 쿠폰 상세 정보 (V1 래핑) */
  getCouponDetail: (data: Record<string, unknown>): Promise<{ data: Record<string, unknown> }> =>
    SuperFetch.post('/coupon/detail', setRequestForm(data, true)),

  /** [쿠폰 상세] 쿠폰 사용 내역 리스트 (V1 래핑) */
  getCouponUsageHistoryList: (data: Record<string, unknown>): Promise<{
    data: { couponUsageHistoryList: Record<string, unknown>[]; totalCnt: number };
  }> => SuperFetch.post('/coupon/detail/usage/history', setRequestForm(data, true)),

  /** [쿠폰 상세] 쿠폰 사용 내역 엑셀 다운로드 (V1 래핑) */
  getCouponUsageHistoryListExcelDownload: (data: Record<string, unknown>): Promise<{
    data: { couponUsageHistoryListExcelSheet: { data: number[] } };
  }> => SuperFetch.post('/coupon/detail/usage/history/excel/download', setRequestForm(data, true)),

  /** [쿠폰 상세] 쿠폰 미사용 내역 리스트 (V1 래핑) */
  getCouponUnUsageHistoryList: (data: Record<string, unknown>): Promise<{
    data: { couponUnUsageHistoryList: Record<string, unknown>[]; totalCnt: number };
  }> => SuperFetch.post('/coupon/detail/unUsage/history', setRequestForm(data, true)),

  /** [쿠폰 상세] 쿠폰 미사용 내역 엑셀 다운로드 (V1 래핑) */
  getCouponUnUsageHistoryListExcelDownload: (data: Record<string, unknown>): Promise<{
    data: { couponUnUsageHistoryListExcelSheet: { data: number[] } };
  }> => SuperFetch.post('/coupon/detail/unUsage/history/excel/download', setRequestForm(data, true)),

  /** [쿠폰 상세] 쿠폰 적용 가능 상품 리스트 (V1 래핑) */
  getCouponProductLinkList: (data: Record<string, unknown>): Promise<{
    data: { couponProductLinkList: Record<string, unknown>[]; totalCnt: number };
  }> => SuperFetch.post('/coupon/detail/productLink', setRequestForm(data, true)),

  /** [쿠폰 상세] 쿠폰 적용 가능 브랜드 리스트 (V1 래핑) */
  getCouponBrandLinkList: (data: Record<string, unknown>): Promise<{
    data: { couponBrandLinkList: Record<string, unknown>[]; totalCnt: number };
  }> => SuperFetch.post('/coupon/detail/brandLink', setRequestForm(data, true)),

  /** [쿠폰 생성] 쿠폰 생성하기 (V1 래핑) */
  insertCoupon: (data: Record<string, unknown>): Promise<{ msg?: string; data: { couponId: number } }> =>
    SuperFetch.post('/coupon/insert', setRequestForm(data, true)),

  /** [쿠폰 편집] 쿠폰 편집하기 (V1 래핑) */
  editCoupon: (data: Record<string, unknown>): Promise<{ msg?: string; data: Record<string, unknown> }> =>
    SuperFetch.post('/coupon/edit', setRequestForm(data, true)),

  /** [쿠폰 편집/복사] 쿠폰 정보 가져오기 (V1 래핑) */
  getCouponInfo: (data: Record<string, unknown>): Promise<{ msg?: string; data: Record<string, unknown> }> =>
    SuperFetch.post('/coupon/detail/info', setRequestForm(data, true)),

  /** [쿠폰 생성] 쿠폰 적용 가능 상품 아이템 리스트 (V1 래핑) */
  getCouponProductItemList: (data: Record<string, unknown>): Promise<{
    msg?: string;
    data: {
      couponProductLinkInfo: { list: Record<string, unknown>[]; totalCnt: number };
      mainProductCategorySelectList: Record<string, unknown>[];
      subProductCategorySelectList: Record<string, unknown>;
    };
  }> => SuperFetch.post('/coupon/insert/preparation/product/list', setRequestForm(data, true)),

  /** [쿠폰 생성] 쿠폰 적용 가능 브랜드 아이템 리스트 (V1 래핑) */
  getCouponBrandItemList: (data: Record<string, unknown>): Promise<{
    msg?: string;
    data: {
      couponBrandLinkInfo: { list: Record<string, unknown>[]; totalCnt: number };
    };
  }> => SuperFetch.post('/coupon/insert/preparation/brand/list', setRequestForm(data, true)),

  /** [쿠폰 생성] 상품 아이템 리스트 엑셀 다운로드 (V1 래핑) */
  getCouponProductItemListExcelDownload: (data: Record<string, unknown>): Promise<{
    msg?: string;
    data: { couponProductItemExcelSheet: { data: number[] } };
  }> => SuperFetch.post('/coupon/insert/preparation/product/list/excel/download', setRequestForm(data, true)),

  /** [쿠폰 생성] 선택된 상품 아이템 리스트 엑셀 다운로드 (V1 래핑) */
  getSelectedCouponProductItemListExcelDownload: (data: Record<string, unknown>): Promise<{
    msg?: string;
    data: { selectedCouponProductItemExcelSheet: { data: number[] } };
  }> => SuperFetch.post('/coupon/insert/preparation/product/list/excel/download/selected', setRequestForm(data, true)),

  /** [쿠폰 생성] 상품 리스트 엑셀 업로드 유효성 체크 (V1 래핑) */
  checkValidCouponProductItemListExcelUpload: (data: Record<string, unknown>): Promise<{
    msg?: string;
    data: Record<string, unknown>;
  }> => SuperFetch.post('/coupon/insert/preparation/product/list/excel/upload/check', setMultipartForm(data, true)),

  /** [쿠폰 생성] 브랜드 아이템 리스트 엑셀 다운로드 (V1 래핑) */
  getCouponBrandItemListExcelDownload: (data: Record<string, unknown>): Promise<{
    msg?: string;
    data: { couponBrandItemExcelSheet: { data: number[] } };
  }> => SuperFetch.post('/coupon/insert/preparation/brand/list/excel/download', setRequestForm(data, true)),

  /** [쿠폰 생성] 선택된 브랜드 아이템 리스트 엑셀 다운로드 (V1 래핑) */
  getSelectedCouponBrandItemListExcelDownload: (data: Record<string, unknown>): Promise<{
    msg?: string;
    data: { selectedCouponBrandItemExcelSheet: { data: number[] } };
  }> => SuperFetch.post('/coupon/insert/preparation/brand/list/excel/download/selected', setRequestForm(data, true)),

  /** [쿠폰 생성] 브랜드 리스트 엑셀 업로드 유효성 체크 (V1 래핑) */
  checkValidCouponBrandItemListExcelUpload: (data: Record<string, unknown>): Promise<{
    msg?: string;
    data: Record<string, unknown>;
  }> => SuperFetch.post('/coupon/insert/preparation/brand/list/excel/upload/check', setMultipartForm(data, true)),

};
